import React, { useState } from 'react';
import { Check, Copy, LifeBuoy, Mail, MessageCircle } from 'lucide-react';
import type { RoutySupportArtifact } from '../../business/routy/routyArtifacts';
import { ROUTEMIZE_SUPPORT_EMAIL } from '../../business/routy/routyTools';
import { useIntercom } from '../shared/IntercomProvider';

interface SupportCardProps {
  artifact: RoutySupportArtifact;
  /** Called after the Support Chat opens (the panel closes itself so the
   * messenger — which occupies the same corner — isn't hidden behind it). */
  onOpenedSupportChat?: () => void;
}

/**
 * The "reach a human" card Routy shows for support/escalation requests: opens
 * the in-app Support Chat (the same Intercom messenger as the sidebar "Help"
 * button) and offers the official support email — the app's REAL channels, so
 * the model never has to improvise contact details.
 *
 * When Routy captured a context snapshot (what failed, exact error), opening
 * the chat attempts to prefill the composer AND copies the snapshot to the
 * clipboard — Intercom workflows/bots that intercept new conversations ignore
 * `showNewMessage` prefill, so the user can just paste. A dedicated "Copy
 * details" button covers re-copying later in the conversation.
 */
const SupportCard: React.FC<SupportCardProps> = ({ artifact, onOpenedSupportChat }) => {
  const { openIntercom, openIntercomWithMessage } = useIntercom();
  const [copied, setCopied] = useState(false);

  const contextPayload = artifact.contextSummary
    ? [`Routy escalation${artifact.topic ? `: ${artifact.topic}` : ''}`, '', artifact.contextSummary].join('\n')
    : null;

  const copyContext = async (): Promise<void> => {
    if (!contextPayload) return;
    try {
      await navigator.clipboard.writeText(contextPayload);
      setCopied(true);
      setTimeout(() => setCopied(false), 2500);
    } catch {
      // Clipboard unavailable — the prefill attempt below still applies.
    }
  };

  const handleOpenChat = () => {
    if (contextPayload) {
      // Best-effort composer prefill; a bot/workflow greeting can swallow it,
      // so the clipboard copy is the reliable path.
      void copyContext();
      openIntercomWithMessage(contextPayload);
    } else {
      openIntercom();
    }
    onOpenedSupportChat?.();
  };

  return (
    <div className="ml-10 rounded-lg bg-surface border border-ink-200 shadow-sm overflow-hidden max-w-sm">
      <div className="px-4 py-2.5 bg-ink-50 border-b border-ink-200 flex items-center gap-2">
        <LifeBuoy className="w-4 h-4 text-blue-600" />
        <span className="text-sm font-semibold text-ink-900 truncate">Talk to our team</span>
      </div>

      <div className="p-3 space-y-2.5">
        {artifact.topic && <p className="text-xs text-ink-500">About: {artifact.topic}</p>}
        <p className="text-xs text-ink-600 leading-snug">
          The Support Chat is the fastest way to reach us — the team can see your account and help directly.
        </p>
        <button
          onClick={handleOpenChat}
          className="w-full flex items-center justify-center gap-2 py-2.5 rounded-lg bg-blue-600 text-white text-sm font-semibold hover:bg-blue-700 active:scale-[0.99] transition-all"
        >
          <MessageCircle className="w-4 h-4" />
          Open Support Chat
        </button>
        {contextPayload && (
          <>
            <button
              onClick={() => void copyContext()}
              className="w-full flex items-center justify-center gap-2 py-2 rounded-lg border border-ink-200 text-ink-700 text-xs font-medium hover:bg-ink-50 transition-colors"
            >
              {copied ? <Check className="w-3.5 h-3.5 text-emerald-600" /> : <Copy className="w-3.5 h-3.5" />}
              {copied ? 'Copied — paste it into the chat' : 'Copy details for support'}
            </button>
            <p className="text-2xs text-ink-400 leading-snug">
              Opening the chat copies Routy's captured details automatically — paste them in if they don't
              appear in the message box.
            </p>
          </>
        )}
        <a
          href={`mailto:${ROUTEMIZE_SUPPORT_EMAIL}`}
          className="w-full flex items-center justify-center gap-2 py-2 rounded-lg border border-ink-200 text-ink-700 text-xs font-medium hover:bg-ink-50 transition-colors"
        >
          <Mail className="w-3.5 h-3.5" />
          {ROUTEMIZE_SUPPORT_EMAIL}
        </a>
      </div>
    </div>
  );
};

export default SupportCard;
